import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { redirectExternal } from "@/lib/redirect";
import { toast } from "sonner";
import {
  CHARTER_CLIENT_ANNUAL_PRICE,
  CHARTER_CLIENT_BOOKING_FEE,
  CHARTER_CLIENT_LOOKUP_KEY,
} from "@/data/pjp";
import { peekPendingRfq } from "@/lib/pendingRfq";
import {
  ArrowRight, Check, Globe, Users, ShieldCheck, Plane, Loader2,
  Radio, Sparkles, Building2, Handshake, X as XIcon,
} from "lucide-react";

/**
 * /charter-membership
 *
 * Upsell landing page reached from the "Get Operator Direct Bids" CTA on
 * every quote result. Prospects who click the CTA without a Charter Client
 * membership land here — where we make the pitch and route them to Stripe
 * checkout via the standard /payments/checkout flow (same one Membership.jsx
 * uses).
 *
 * If the user is already signed in with the charter-client tier, we still
 * render the page (they can re-read the value prop) but we short-circuit the
 * primary CTA to "File your RFQ" instead of checkout.
 */
export default function CharterMembership() {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);

  // The RFQ CTA passes ?from=quote with the aircraft type so we can address
  // the shopper by exactly what they were pricing.
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const typeCode = params.get("type") || "";
  const routeLabel = params.get("route") || "";

  // Membership is proven by the paid Stripe subscription flag — NOT by the
  // hat/role the user picked at signup (that's just their stated intent).
  // The webhook flips `membership_tier_slug` to "charter_client" after a
  // successful charge; that's the ground truth here.
  const isCharterMember = user?.membership_tier_slug === "charter_client";

  // Anonymous trip preserved by /quote → widget stashPendingRfq() call.
  // If present we surface a "your trip is safe" chip so the shopper can
  // commit to pay knowing they won't have to redo the form.
  const pendingRfq = peekPendingRfq();

  const startCheckout = async () => {
    if (!user) {
      // Sign up first, then bounce back with the pending checkout key.
      sessionStorage.setItem("pjp_pending_checkout", CHARTER_CLIENT_LOOKUP_KEY);
      nav("/register?tier=charter-client");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", {
        lookup_key: CHARTER_CLIENT_LOOKUP_KEY,
        origin_url: window.location.origin,
      });
      if (!redirectExternal(r.data.checkout_url)) {
        setBusy(false);
        toast.error("Popup blocked — allow pop-ups and try again.");
      }
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      {/* Hero */}
      <div className="mb-8">
        <Link to="/quote" data-testid="charter-membership-back-quote" className="mono text-[10px] tracking-widest uppercase text-cyan/70 hover:text-cyan inline-flex items-center gap-1">
          ← Back to quote
        </Link>
      </div>

      <div
        className="relative overflow-hidden rounded-2xl border border-white/10 p-8 md:p-14"
        style={{ background: "radial-gradient(circle at 15% 20%, rgba(75,156,255,0.18), transparent 55%), linear-gradient(180deg, rgba(5,8,16,0.9), rgba(5,8,16,0.75))" }}
        data-testid="charter-membership-hero"
      >
        {/* HUD scanline overlay */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay"
          style={{ backgroundImage: "repeating-linear-gradient(0deg, rgba(75,156,255,0.6) 0px, rgba(75,156,255,0.6) 1px, transparent 1px, transparent 3px)" }}
        />

        <div className="relative max-w-3xl">
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">
            // Oh man — you aren't a PJP member <span className="text-white/60">just yet</span>
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tighter leading-[0.95] mt-3 drop-shadow-[0_2px_20px_rgba(0,0,0,0.6)]">
            Sign up and get PJP's <span className="text-cyan">industry volume pricing</span>.
          </h1>
          {typeCode && (
            <div className="mt-4 mono text-[11px] tracking-widest uppercase text-white/60" data-testid="charter-membership-context">
              Quoted trip · <span className="text-cyan">{typeCode}</span>{routeLabel && <> · {routeLabel}</>} — a Charter Client membership unlocks direct-to-operator bids on this trip and every future one.
            </div>
          )}

          {pendingRfq && (
            <div
              className="mt-5 inline-flex items-center gap-2 rounded-md border border-avail/40 bg-avail/[0.08] px-3 py-2"
              data-testid="charter-membership-rfq-preserved"
            >
              <Radio className="h-3.5 w-3.5 text-avail animate-pulse" />
              <span className="mono text-[10px] tracking-[0.24em] uppercase text-avail">// Trip preserved</span>
              <span className="text-[12px] text-white/80">
                Your <span className="text-cyan">{pendingRfq.route_label || `${pendingRfq.departure_icao} → ${pendingRfq.destination_icao}`}</span> RFQ is waiting — it fires the moment you finish signup.
              </span>
            </div>
          )}

          <p className="mt-6 text-slate-200 text-lg max-w-2xl">
            PJP offers charter pricing from <b className="text-cyan">thousands of aircraft</b> flying all over the world —
            never in the same place when you need them. We source those airplanes,
            have operators <b className="text-cyan">bid live on your trip</b>, and hand you the best options at the best price.
            PJP does all the work for you.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            {isCharterMember ? (
              <button
                onClick={() => nav("/charter-dashboard")}
                data-testid="charter-membership-file-rfq"
                className="btn-hud btn-hud-solid"
              >
                <Radio className="h-3.5 w-3.5" /> OPEN YOUR DASHBOARD <ArrowRight className="h-3.5 w-3.5" />
              </button>
            ) : (
              <>
                <button
                  onClick={startCheckout}
                  disabled={busy}
                  data-testid="charter-membership-checkout"
                  className="btn-hud btn-hud-solid disabled:opacity-50"
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  {busy ? "OPENING CHECKOUT…" : `JOIN · $${CHARTER_CLIENT_ANNUAL_PRICE.toLocaleString()}/YR`}
                  {!busy && <ArrowRight className="h-3.5 w-3.5" />}
                </button>
                <Link
                  to="/contact"
                  data-testid="charter-membership-talk-sales"
                  className="btn-hud"
                >
                  <Users className="h-3.5 w-3.5" /> TALK TO CONCIERGE
                </Link>
              </>
            )}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-4 text-[11px] text-white/60">
            <span className="mono uppercase tracking-widest text-white/50">// Members-only network:</span>
            <span className="flex items-center gap-1"><Globe className="h-3 w-3 text-cyan" /> Thousands of operator aircraft worldwide</span>
            <span className="flex items-center gap-1"><Handshake className="h-3 w-3 text-avail" /> Live operator bids, not stale rate cards</span>
          </div>
        </div>
      </div>

      {/* Value prop */}
      <div className="mt-10 grid md:grid-cols-3 gap-4" data-testid="charter-membership-value-grid">
        <ValueCard
          icon={Globe}
          title="A jet is never where you need it"
          body="Private aircraft criss-cross the globe every hour of every day. The one that fits your trip is almost never sitting at your origin. PJP sees the whole fleet — not one operator's dozen tails."
          tone="cyan"
        />
        <ValueCard
          icon={Radio}
          title="Operators bid — you don't guess"
          body="Every trip you file goes out to the operators flying your requested type. They bid against each other in real time. You see the spread on your PJP quote card and pick the winner."
          tone="avail"
        />
        <ValueCard
          icon={ShieldCheck}
          title="Volume pricing your $100M trip can't match"
          body="26,500+ members and hundreds of trips a month give PJP institutional buying leverage no single flyer has. Members get pricing operators won't publish — full stop."
          tone="cyan"
        />
      </div>

      {/* Membership card */}
      <div
        className="mt-10 rounded-2xl border border-cyan/40 bg-cyan/[0.05] p-8 md:p-10 grid md:grid-cols-[1.3fr_1fr] gap-8"
        data-testid="charter-membership-price-card"
      >
        <div>
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// Charter Client Membership</div>
          <div className="font-display text-6xl font-bold text-white mt-2 leading-none">
            ${CHARTER_CLIENT_ANNUAL_PRICE.toLocaleString()}<span className="text-2xl text-white/60 font-normal"> / year</span>
          </div>
          <div className="mono text-[11px] tracking-widest uppercase text-white/60 mt-2">
            + ${CHARTER_CLIENT_BOOKING_FEE} flat booking fee per flight (only if PJP handles logistics)
          </div>

          <ul className="mt-6 space-y-2 text-[13px] text-white/85" data-testid="charter-membership-perks">
            {[
              "Broadcast every trip to every PJP operator flying your requested type",
              "Live operator bids delivered to your inbox — 20 to 90 min turnaround",
              "PJP-negotiated volume pricing on top of the operator bids",
              "$250 flat booking fee if PJP handles the whole thing — contract, deposit, catering, ground",
              "Or deal direct with the operator at zero markup — you choose",
              "Priority JumpSeat alerts before they hit the public marketplace",
              "Dedicated concierge for AOG, catering, ground, and international handling",
            ].map((p, i) => (
              <li key={i} className="flex gap-2">
                <Check className="h-4 w-4 text-avail shrink-0 mt-0.5" /> <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col justify-between gap-4">
          {/* Booking option compare */}
          <div className="rounded-lg border border-white/10 p-4 bg-obsidian/60" data-testid="charter-membership-options">
            <div className="mono text-[10px] tracking-widest uppercase text-cyan/80 mb-2">// Two ways to book</div>
            <BookingOption
              icon={Building2}
              label="PJP handles it end-to-end"
              price={`$${CHARTER_CLIENT_BOOKING_FEE} / flight`}
              body="Contract, deposit, crew coordination, catering, ground, customs — hands-off booking."
            />
            <div className="my-3 border-t border-white/10" />
            <BookingOption
              icon={Handshake}
              label="Deal direct with the operator"
              price="$0"
              body="PJP surfaces the bids, you close it. Zero markup, zero fee."
            />
          </div>

          {!isCharterMember && (
            <button
              onClick={startCheckout}
              disabled={busy}
              data-testid="charter-membership-checkout-secondary"
              className="btn-hud btn-hud-solid disabled:opacity-50 w-full justify-center"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {busy ? "OPENING CHECKOUT…" : "JOIN NOW"} <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* First-year savings calculator */}
      <SavingsCalculator onJoin={startCheckout} busy={busy} isMember={isCharterMember} />

      {/* How it works timeline */}
      <div className="mt-12" data-testid="charter-membership-how">
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80 mb-4">// How it works · 4 steps</div>
        <div className="grid md:grid-cols-4 gap-3">
          <HowStep n="01" title="Price your trip on PJP" body="Use the same instant-quote widget you're on now to get a 2026 market band." />
          <HowStep n="02" title="Click Get Operator Direct Bids" body="Members' click fires the trip to every operator flying that aircraft type." />
          <HowStep n="03" title="Bids land in your inbox" body="Operators quote against each other. Typically 20-90 min turnaround in business hours." />
          <HowStep n="04" title="Pick your flight" body="Let PJP close it for you ($250), or deal direct with the operator for free." />
        </div>
      </div>

      <div className="mt-10 text-center">
        <div className="mono text-[10px] tracking-widest uppercase text-white/40">
          Not ready to join? <Link to="/quote" data-testid="charter-membership-back-quote-2" className="text-cyan hover:text-white underline decoration-cyan/40">Keep pricing trips anonymously</Link> · we'll be here when you are.
        </div>
      </div>
    </div>
  );
}

function ValueCard({ icon: Icon, title, body, tone }) {
  const ringCls = tone === "avail" ? "border-avail/30 bg-avail/[0.04]" : "border-cyan/25 bg-cyan/[0.04]";
  const iconCls = tone === "avail" ? "text-avail" : "text-cyan";
  return (
    <div className={`rounded-xl border ${ringCls} p-5`}>
      <Icon className={`h-6 w-6 ${iconCls}`} />
      <div className="font-display text-xl font-bold text-white mt-3 leading-tight">{title}</div>
      <div className="text-[13px] text-white/70 mt-2 leading-relaxed">{body}</div>
    </div>
  );
}

function BookingOption({ icon: Icon, label, price, body }) {
  return (
    <div className="flex gap-3">
      <div className="p-1.5 rounded bg-cyan/10 border border-cyan/30 h-fit">
        <Icon className="h-4 w-4 text-cyan" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <div className="font-display text-sm font-bold text-white">{label}</div>
          <div className="mono text-[11px] tracking-widest uppercase text-cyan ml-auto">{price}</div>
        </div>
        <div className="text-[12px] text-white/65 mt-1 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

function HowStep({ n, title, body }) {
  return (
    <div className="rounded-lg border border-white/10 p-4 bg-white/[0.02]">
      <div className="mono text-[9px] tracking-[0.24em] uppercase text-cyan/70">STEP {n}</div>
      <div className="font-display text-base font-bold text-white mt-1">{title}</div>
      <div className="text-[12px] text-white/65 mt-1.5 leading-relaxed">{body}</div>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * SavingsCalculator
 *
 * Live ROI widget. Anchor: PJP's operator-bid model saves 12–18% against
 * single-vendor retail (empirical from the KOPF quote comparison you shared:
 * lowest $123k vs highest $226k on the same trip → 45% swing at the extremes;
 * we use a conservative 12–18% assuming the median flyer buys mid-band).
 *
 * Inputs:
 *   • annual charter spend   (default $500k, 50k – 10M)
 *   • trips per year         (default 8,   1 – 100)
 *   • "PJP books everything" toggle → adds $250/flight to the cost side
 *
 * Outputs:
 *   • Volume savings low/median/high band
 *   • Membership + optional booking fees deducted
 *   • Net first-year saving band
 *   • ROI multiple against the $5k membership
 * ---------------------------------------------------------------------- */

const SAVINGS_LOW_PCT   = 0.12;
const SAVINGS_MED_PCT   = 0.15;
const SAVINGS_HIGH_PCT  = 0.18;

function SavingsCalculator({ onJoin, busy, isMember }) {
  const [spend, setSpend] = useState(500_000);
  const [trips, setTrips] = useState(8);
  const [pjpBooks, setPjpBooks] = useState(true);   // whether PJP handles logistics (adds $250/flight)

  const numFmt = (n) => n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  const gross = {
    low:    spend * SAVINGS_LOW_PCT,
    median: spend * SAVINGS_MED_PCT,
    high:   spend * SAVINGS_HIGH_PCT,
  };
  const membershipCost = CHARTER_CLIENT_ANNUAL_PRICE;
  const bookingFees = pjpBooks ? trips * CHARTER_CLIENT_BOOKING_FEE : 0;
  const totalCost = membershipCost + bookingFees;

  const net = {
    low:    Math.round(gross.low    - totalCost),
    median: Math.round(gross.median - totalCost),
    high:   Math.round(gross.high   - totalCost),
  };
  const roiMultiple = net.median / membershipCost;   // net gain per membership dollar

  return (
    <div
      className="mt-10 rounded-2xl border border-avail/30 bg-gradient-to-br from-avail/[0.06] to-cyan/[0.03] p-8"
      data-testid="charter-membership-savings-calc"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-avail">// First-year savings calculator</div>
        <div className="mono text-[9px] tracking-widest uppercase text-white/40">
          Anchored to 12–18% operator-bid savings · empirical from PJP RFQ samples
        </div>
      </div>
      <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white mt-1">
        A <span className="text-cyan">$5,000</span> decision that pays for itself before your <span className="text-avail">second trip</span>.
      </h3>

      <div className="mt-6 grid md:grid-cols-[1fr_1.3fr] gap-6">
        {/* Inputs */}
        <div className="space-y-5">
          <SliderRow
            label="Your annual charter spend"
            value={spend}
            onChange={setSpend}
            min={50_000}
            max={10_000_000}
            step={25_000}
            format={numFmt}
            testId="savings-spend"
          />
          <SliderRow
            label="Trips per year"
            value={trips}
            onChange={setTrips}
            min={1}
            max={100}
            step={1}
            format={(v) => `${v} trip${v !== 1 ? "s" : ""}`}
            testId="savings-trips"
          />

          <label className="flex items-center gap-3 mt-3 cursor-pointer group" data-testid="savings-pjp-books">
            <input
              type="checkbox"
              checked={pjpBooks}
              onChange={(e) => setPjpBooks(e.target.checked)}
              className="h-4 w-4 accent-cyan cursor-pointer"
            />
            <span className="text-[13px] text-white/85 group-hover:text-white">
              PJP handles every booking · adds <b className="mono text-cyan">${CHARTER_CLIENT_BOOKING_FEE}</b>/flight
            </span>
          </label>
          <div className="text-[11px] text-white/50 pl-7">
            Toggle off to deal direct with operators — <b className="text-avail">zero markup, zero booking fee</b>. Members can mix per trip.
          </div>
        </div>

        {/* Output card */}
        <div className="rounded-xl border border-avail/40 bg-obsidian/60 p-5" data-testid="savings-output">
          {/* Big net saving */}
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-avail">// Your net first-year saving</div>
          <div className="mt-2 flex items-baseline gap-2 flex-wrap">
            <div className="font-display text-4xl md:text-5xl font-bold text-avail leading-none" data-testid="savings-net-median">
              {numFmt(Math.max(0, net.median))}
            </div>
            <div className="mono text-[11px] tracking-widest uppercase text-white/50">
              median · range <span className="text-avail">{numFmt(Math.max(0, net.low))}</span> – <span className="text-avail">{numFmt(Math.max(0, net.high))}</span>
            </div>
          </div>
          <div className="mt-1 mono text-[11px] tracking-widest uppercase text-white/60">
            <span className="text-cyan">{roiMultiple >= 0 ? roiMultiple.toFixed(1) : "0.0"}×</span> return on your $5k membership
          </div>

          {/* Breakdown */}
          <div className="mt-5 space-y-2 text-[13px]">
            <BreakdownRow
              label={`Volume savings on ${numFmt(spend)}`}
              detail={`${(SAVINGS_LOW_PCT * 100).toFixed(0)}–${(SAVINGS_HIGH_PCT * 100).toFixed(0)}% band`}
              value={`+${numFmt(gross.median)}`}
              tone="text-avail"
              testId="savings-gross"
            />
            <BreakdownRow
              label="Charter Client membership"
              detail="annual"
              value={`-${numFmt(membershipCost)}`}
              tone="text-white/85"
              testId="savings-membership-line"
            />
            {pjpBooks && (
              <BreakdownRow
                label={`Booking fees · ${trips} trip${trips !== 1 ? "s" : ""}`}
                detail={`${trips} × $${CHARTER_CLIENT_BOOKING_FEE}`}
                value={`-${numFmt(bookingFees)}`}
                tone="text-white/85"
                testId="savings-booking-line"
              />
            )}
            <div className="border-t border-white/10 pt-2 flex items-baseline justify-between">
              <div className="mono text-[10px] tracking-widest uppercase text-white/60">Net year-one</div>
              <div className="mono text-[14px] text-avail font-bold" data-testid="savings-net-line">
                {net.median >= 0 ? "+" : ""}{numFmt(net.median)}
              </div>
            </div>
          </div>

          {/* Framing */}
          {net.median > 0 && (
            <div className="mt-4 text-[12px] text-white/70 leading-relaxed">
              A <b className="text-cyan">$5,000</b> membership that returns <b className="text-avail">{numFmt(net.median)}</b> in year one — before we count JumpSeat alerts, concierge time, or the priority you'll get on AOG blasts.
            </div>
          )}

          {!isMember && (
            <button
              onClick={onJoin}
              disabled={busy}
              data-testid="savings-join-cta"
              className="btn-hud btn-hud-solid mt-5 w-full justify-center disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {busy ? "OPENING CHECKOUT…" : `LOCK IN ${numFmt(Math.max(0, net.median))} · JOIN NOW`} <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 text-[10px] mono tracking-widest uppercase text-white/35">
        // 12–18% band derived from real PJP RFQ samples where the same trip returned 5 operator quotes spanning ~$103k. Individual results vary with route, aircraft, and season.
      </div>
    </div>
  );
}

function SliderRow({ label, value, onChange, min, max, step, format, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="mono text-[10px] tracking-widest uppercase text-white/60">{label}</div>
        <div className="mono text-[14px] text-cyan">{format(value)}</div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-cyan cursor-pointer"
        data-testid={`${testId}-slider`}
      />
      <div className="flex justify-between mono text-[9px] tracking-widest uppercase text-white/30 mt-1">
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  );
}

function BreakdownRow({ label, detail, value, tone, testId }) {
  return (
    <div className="flex items-baseline justify-between" data-testid={testId}>
      <div className="flex-1 min-w-0">
        <div className="text-white/85 text-[13px]">{label}</div>
        <div className="mono text-[9px] tracking-widest uppercase text-white/40">{detail}</div>
      </div>
      <div className={`mono text-[13px] ${tone}`}>{value}</div>
    </div>
  );
}
